import { useCallback, useMemo, useState } from "react";

import { useOrders, useRouteMutation } from "@web-app/api";
import { useAlert } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";

export default function useManageRoute() {
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isSuccess } = useOrders();
  const orders = data?.orders;
  const { focusedRouteId, routeModalMode } = useDispatchStore();
  const { removeRoute } = useRouteMutation();
  const { showAlertAsync } = useAlert();

  const setIsEditing = useCallback((isEditing: boolean) => {
    if (isEditing) {
      useDispatchStore.setState({
        routeModalMode: "edit",
      });
    } else {
      useDispatchStore.setState({
        editRouteOrderIds: [],
        routeModalMode: "closed",
      });
    }
  }, []);

  const setIsCreating = useCallback((isCreating: boolean) => {
    if (isCreating) {
      useDispatchStore.setState({
        editRouteOrderIds: [],
        focusedRouteId: "new",
        routeModalMode: "create",
      });
    } else {
      useDispatchStore.setState({
        editRouteOrderIds: [],
        focusedRouteId: null,
        routeModalMode: "closed",
      });
    }
  }, []);

  const onRouteOrdersCardViewDetails = (orderId: string) => {
    useDispatchStore.setState({
      focusedOrderId: orderId,
    });
  };

  const route = useMemo(() => {
    if (!focusedRouteId || !orders) return null;

    const routeOrders = Array.from(orders?.values() ?? [])
      .filter((order) => order.routeId === focusedRouteId)
      .sort((a, b) => {
        const aIndex = a.route?.ordersOrder.find((o) => o.id === a.id);
        const bIndex = b.route?.ordersOrder.find((o) => o.id === b.id);

        if (aIndex && bIndex) {
          return aIndex.index - bIndex.index;
        }

        return 0;
      });

    const routeDetails = routeOrders[0]?.route;

    return {
      details: routeDetails,
      orders: routeOrders,
    };
  }, [focusedRouteId, orders]);

  return {
    deleteRoute: removeRoute,
    isCreating: routeModalMode === "edit" && focusedRouteId === "new",
    isDeleting,
    isEditing: routeModalMode === "edit" && focusedRouteId !== "new",
    isSuccess,
    onRouteOrdersCardViewDetails,
    route,
    setIsCreating,
    setIsDeleting,
    setIsEditing,
    showAlertAsync,
  };
}
